import { framedByDefault } from '@utils/frames';
import { TFunction } from 'next-i18next';

import { FlowConfig } from '../../providers';
import { PreAuthDrawer } from '../draws/components';
import { FrameStepDraw } from '../draws/components/FrameStepDraw';
import { LocaleStep } from '../draws/components/LocaleStep';
import { AddPaymentMethod } from '../draws/components/payment-methods/AddPaymentMethod';
import { AddPaymentMethodTitle } from '../draws/components/payment-methods/AddPaymentMethodTitle';
import { PaymentMethodSelector } from '../draws/components/payment-methods/PaymentMethodSelector';
import { PaymentMethodTitle } from '../draws/components/payment-methods/PaymentMethodTitle';
import { AddShippingAddress } from '../draws/components/shipping-address/AddShippingAddress';
import { EditShippingAddress } from '../draws/components/shipping-address/EditShippingAddress';
import { ShippingAddressSelector } from '../draws/components/shipping-address/ShippingAddressSelector';
import { DrawWithFrames } from '../draws/types';
import { BidStep } from './components/BidStep';

export const buildAuctionFlowConfig = (
  t: TFunction,
  drawWithFrames: DrawWithFrames,
): FlowConfig => {
  const { draw, frames, product } = drawWithFrames;

  const auctionSupportsFrame = frames && frames.length > 0;
  const isFramedByDefault = framedByDefault(product.framingOffered);

  return {
    containerId: 'stripe',
    name: 'auctionFlow',
    steps: [
      {
        analytics: {
          name: 'bid',
        },
        content: <BidStep draw={draw} />,
        id: 'bid',
        title:
          draw.allocation > 1
            ? t('drawers.bid.rankedAuctionTitle')
            : t('drawers.bid.singleAuctionTitle'),
      },
      ...(auctionSupportsFrame && !isFramedByDefault
        ? [
            {
              analytics: {
                name: 'frame',
              },
              content: <FrameStepDraw drawWithFrames={drawWithFrames} />,
              id: 'frame-intro',
              title: t('drawers.review.framing.title'),
            },
          ]
        : []),
      {
        analytics: {
          name: 'review',
        },
        forceRender: true,
        content: <PreAuthDrawer drawWithFrames={drawWithFrames} />,
        id: 'apply',
        title: t('subscriptionCta.auction.reviewTitle'),
      },
      {
        analytics: {
          name: 'address',
        },
        backStepId: 'apply',
        content: <ShippingAddressSelector />,
        id: 'select_shipping',
        title: t('drawers.review.draw.address.selectAddress.title'),
      },
      {
        analytics: {
          name: 'add address',
        },
        backStepId: 'apply',
        id: 'add_shipping_address',
        content: <AddShippingAddress />,
        title: t('drawers.review.draw.address.addAddress.title'),
      },
      {
        analytics: {
          name: 'edit address',
        },
        backStepId: 'apply',
        id: 'edit_shipping_address',
        content: <EditShippingAddress />,
        title: t('drawers.review.draw.address.editAddress.title'),
      },
      {
        analytics: {
          name: 'payment',
        },
        backStepId: 'apply',
        id: 'select_payment_method',
        content: <PaymentMethodSelector />,
        title: <PaymentMethodTitle />,
      },
      {
        analytics: {
          name: 'payment',
        },
        forceRender: true,
        backStepId: 'apply',
        id: 'add_payment_method',
        content: <AddPaymentMethod />,
        title: <AddPaymentMethodTitle />,
      },
      {
        analytics: {
          name: 'locale',
        },
        forceRender: true,
        backStepId: 'apply',
        content: <LocaleStep />,
        id: 'locale',
        title: t('footer:drawers.locale.title'),
      },
      {
        analytics: {
          name: 'frame',
        },
        backStepId: 'apply',
        content: <FrameStepDraw drawWithFrames={drawWithFrames} />,
        id: 'frame',
        title: t('drawers.review.framing.title'),
      },
    ],
  };
};
